/**
 * Shared metric-label vocabulary for SQL-view widgets (wizard/config side).
 *
 * Produces the human label for an aggregation metric - e.g. "Count of records",
 * "Sum of Wall Thickness" - from its SQL function + column, honoring a display-name
 * map. The synthetic RowIndex column is the per-row counter, so any metric over it
 * reads "records" rather than the internal column name.
 *
 * Pairing note: dashboard-widget.vue (the runtime render side) keeps its own
 * module-scope metricLabel with the same RowIndex mapping. That helper is
 * deliberately NOT imported here (and this one is not imported there) to avoid a
 * cross-boundary dependency between the config wizard and the runtime widget. If the
 * label vocabulary changes, update both. The widget does import FUNCTION_LABELS and
 * COUNT_DISTINCT from here, so the function list itself is defined once. This module
 * stays dependency-free.
 */

// Persisted token of the distinct-count function (counts each value once).
export const COUNT_DISTINCT = 'COUNT_DISTINCT';

// Display labels for the SQL aggregate functions; the persisted values stay SQL.
export const FUNCTION_LABELS = Object.freeze({
  COUNT: 'Count',
  [COUNT_DISTINCT]: 'Count distinct',
  SUM: 'Sum',
  AVG: 'Average',
  MIN: 'Min',
  MAX: 'Max',
});

// Functions that work on a column of any type; the rest need a numeric column.
export const ANY_TYPE_FUNCTIONS = Object.freeze(['COUNT', COUNT_DISTINCT]);

// The synthetic row-counter column: a metric over it counts records, not a real field.
export const ROW_INDEX_COLUMN = 'RowIndex';

/**
 * Human label for a metric's column. RowIndex -> "records"; otherwise the display
 * name from the map, falling back to the raw column name.
 * @param {string} column - the metric's column name
 * @param {Object} [displayNames] - map of column name -> display name
 * @returns {string}
 */
export function metricColumnLabel(column, displayNames) {
  if (column === ROW_INDEX_COLUMN) return 'records';
  const names = displayNames || {};
  return names[column] || column || '';
}

/**
 * Human label for an aggregation metric, e.g. "Count of records". A distinct count reads
 * "Distinct <column>" (e.g. "Distinct Tag").
 * @param {{ function?: string, column?: string }} metric
 * @param {Object} [displayNames] - map of column name -> display name
 * @returns {string} the label, or '' when metric is missing
 */
export function metricLabel(metric, displayNames) {
  if (!metric) return '';
  const col = metricColumnLabel(metric.column, displayNames);
  if (metric.function === COUNT_DISTINCT && col) return `Distinct ${col}`;
  const fn = FUNCTION_LABELS[metric.function] || metric.function || '';
  return col ? `${fn} of ${col}` : fn;
}
